import { useEffect, useCallback } from 'react';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import clsx from 'clsx';
import type { Slide } from './SlidesSidebar';

interface FullscreenModeProps {
  slides: Slide[];
  currentIndex: number;
  onNavigate: (index: number) => void;
  onExit: () => void;
}

export function FullscreenMode({ slides, currentIndex, onNavigate, onExit }: FullscreenModeProps) {
  const slide = slides[currentIndex];
  const canGoPrev = currentIndex > 0;
  const canGoNext = currentIndex < slides.length - 1;

  const handlePrev = useCallback(() => {
    if (canGoPrev) {
      onNavigate(currentIndex - 1);
    }
  }, [canGoPrev, currentIndex, onNavigate]);

  const handleNext = useCallback(() => {
    if (canGoNext) {
      onNavigate(currentIndex + 1);
    }
  }, [canGoNext, currentIndex, onNavigate]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      switch (e.key) {
        case 'ArrowLeft':
        case 'ArrowUp':
        case 'PageUp':
          e.preventDefault();
          handlePrev();
          break;
        case 'ArrowRight':
        case 'ArrowDown':
        case 'PageDown':
        case ' ':
          e.preventDefault();
          handleNext();
          break;
        case 'Escape':
          e.preventDefault();
          onExit();
          break;
        case 'Home':
          e.preventDefault();
          onNavigate(0);
          break;
        case 'End':
          e.preventDefault();
          onNavigate(slides.length - 1);
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handlePrev, handleNext, onExit, onNavigate, slides.length]);

  // Request fullscreen on mount
  useEffect(() => {
    const elem = document.documentElement;
    if (elem.requestFullscreen) {
      elem.requestFullscreen().catch(() => {
        // Fullscreen request failed, continue without it
      });
    }

    return () => {
      if (document.fullscreenElement) {
        document.exitFullscreen().catch(() => {});
      }
    };
  }, []);

  if (!slide) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black flex flex-col">
      {/* Slide content */}
      <div className="flex-1 flex items-center justify-center cursor-pointer" onClick={handleNext}>
        <div className="w-full h-full max-w-[1920px] max-h-[1080px] bg-surface-secondary flex items-center justify-center">
          {slide.template === 'title' && (
            <div className="text-center p-16">
              <h1 className="text-6xl font-bold text-text-primary">
                {slide.title || 'Senza titolo'}
              </h1>
            </div>
          )}

          {slide.template === 'content' && (
            <div className="w-full h-full p-16 flex flex-col">
              <h1 className="text-5xl font-bold text-text-primary mb-12">
                {slide.title || 'Senza titolo'}
              </h1>
              <div className="flex-1 text-2xl text-text-secondary whitespace-pre-wrap">
                {slide.content}
              </div>
            </div>
          )}

          {slide.template === 'two-column' && (
            <div className="w-full h-full p-16 flex flex-col">
              <h1 className="text-5xl font-bold text-text-primary mb-12">
                {slide.title || 'Senza titolo'}
              </h1>
              <div className="flex-1 flex gap-16">
                {slide.content.split('|||').map((col, idx) => (
                  <div key={idx} className="flex-1 text-xl text-text-secondary whitespace-pre-wrap">
                    {col}
                  </div>
                ))}
              </div>
            </div>
          )}

          {slide.template === 'image' && (
            <div className="w-full h-full p-8 flex flex-col items-center justify-center">
              {slide.content && (
                <img
                  src={slide.content}
                  alt={slide.title}
                  className="max-w-full max-h-[80%] object-contain rounded-lg"
                />
              )}
              {slide.title && <p className="mt-8 text-2xl text-text-secondary">{slide.title}</p>}
            </div>
          )}

          {slide.template === 'blank' && (
            <div className="w-full h-full p-16 flex items-center justify-center">
              <div className="text-2xl text-text-secondary whitespace-pre-wrap text-center">
                {slide.content}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Navigation controls - appear on hover */}
      <div className="absolute inset-x-0 bottom-0 p-6 flex items-center justify-between opacity-0 hover:opacity-100 transition-opacity bg-linear-to-t from-black/80 to-transparent">
        {/* Left side - Previous */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            handlePrev();
          }}
          disabled={!canGoPrev}
          className={clsx(
            'p-3 rounded-full transition-colors',
            canGoPrev
              ? 'bg-white/10 text-white hover:bg-white/20'
              : 'bg-white/5 text-white/30 cursor-not-allowed'
          )}
        >
          <ChevronLeft className="w-8 h-8" />
        </button>

        {/* Center - Progress */}
        <div className="flex items-center gap-4">
          <div className="flex gap-2">
            {slides.map((_, idx) => (
              <button
                key={idx}
                onClick={(e) => {
                  e.stopPropagation();
                  onNavigate(idx);
                }}
                className={clsx(
                  'w-2 h-2 rounded-full transition-all',
                  idx === currentIndex ? 'bg-white w-8' : 'bg-white/30 hover:bg-white/50'
                )}
              />
            ))}
          </div>
          <span className="text-white/70 text-sm ml-4">
            {currentIndex + 1} / {slides.length}
          </span>
        </div>

        {/* Right side - Next / Exit */}
        <div className="flex items-center gap-2">
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleNext();
            }}
            disabled={!canGoNext}
            className={clsx(
              'p-3 rounded-full transition-colors',
              canGoNext
                ? 'bg-white/10 text-white hover:bg-white/20'
                : 'bg-white/5 text-white/30 cursor-not-allowed'
            )}
          >
            <ChevronRight className="w-8 h-8" />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onExit();
            }}
            className="p-3 rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors ml-4"
            title="Esci (Esc)"
          >
            <X className="w-6 h-6" />
          </button>
        </div>
      </div>

      {/* Keyboard hint */}
      <div className="absolute top-4 right-4 opacity-0 hover:opacity-100 transition-opacity">
        <div className="px-3 py-2 rounded-lg bg-white/10 text-white/70 text-xs">
          Usa ← → per navigare • Esc per uscire
        </div>
      </div>
    </div>
  );
}
